import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Package2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { formatDate, SAMPLE_STATUSES } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Samples() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { divisionParam } = useDivision();
  const [status, setStatus] = useState("");

  const canManage = ["super_admin", "sales_head", "sales_manager", "sales_coordinator", "factory"].includes(user?.role);
  const statuses = SAMPLE_STATUSES;

  const { data: samples = [], isLoading } = useQuery({
    queryKey: ["samples", divisionParam, status],
    queryFn: async () => (await api.get("/samples", { params: { division_id: divisionParam, status } })).data,
  });

  const setSampleStatus = async (id, newStatus) => {
    try {
      await api.put(`/samples/${id}/status`, { status: newStatus });
      qc.invalidateQueries({ queryKey: ["samples"] });
      toast.success(`Marked ${newStatus}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Sample Requests" subtitle="1-click sample requests from dealers and the sales team — track dispatch to delivery.">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="sample-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : samples.length === 0 ? (
        <Card className="p-12 text-center">
          <Package2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No sample requests yet. Use the “Request Sample” button on a lead or deal.</p>
        </Card>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Request #", "Company", "Product", "Type", "Qty", "Requested By", "Date", "Status"].map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {samples.map((s) => (
                  <TableRow key={s.id} data-testid={`sample-row-${s.id}`}>
                    <TableCell className="font-mono text-sm font-medium">{s.request_number}</TableCell>
                    <TableCell className="text-slate-700">{s.company || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{s.product_name || "Sample kit"}</TableCell>
                    <TableCell><span className={`text-xs font-semibold ${s.sample_type === "Paid" ? "text-amber-700" : "text-emerald-700"}`}>{s.sample_type}</span></TableCell>
                    <TableCell className="text-slate-600">{s.quantity}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{s.requested_by_name}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDate(s.created_at)}</TableCell>
                    <TableCell>
                      {canManage ? (
                        <Select value={s.status} onValueChange={(v) => setSampleStatus(s.id, v)}>
                          <SelectTrigger className="h-8 w-[140px]" data-testid={`sample-status-${s.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <StatusBadge kind="sample" value={s.status} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
